import { Funcionalidade, type Grupo, TipoAcesso } from '@/core/types/graphql'
import { OptionSelect } from '@/core/utils/types'
import type { CargoUsuarioForm } from '../types/cargo'

export const normalizeText = (text?: string) => {
  return (text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

export const funcionalidadeHasAnyTipoAcessoId = (
  funcionalidade: Funcionalidade,
  selectedTiposAcesso: OptionSelect[] | string[]
): boolean => {
  const idsTiposAcesso =
    Array.isArray(selectedTiposAcesso) &&
    typeof selectedTiposAcesso[0] === 'string'
      ? (selectedTiposAcesso as string[])
      : selectedTiposAcesso.map(item => (item as OptionSelect).value)

  return (funcionalidade?.tiposAcesso ?? [])
    .map(tipoAcesso => tipoAcesso?.id)
    .some(id => idsTiposAcesso.indexOf(String(id)) >= 0)
}

export const filterFuncionalidadeByTipoAcesso = (
  selectedTiposAcessos: OptionSelect[],
  funcionalidades?: Funcionalidade[]
) =>
  (funcionalidades ?? []).filter(funcionalidade =>
    funcionalidadeHasAnyTipoAcessoId(funcionalidade, selectedTiposAcessos)
  )

export const getFuncionalidadesPadraoByTipoAcesso = (
  tiposAcessoSelected: OptionSelect[],
  currentSelectedTiposAcessos: OptionSelect[],
  tiposAcesso: TipoAcesso[],
  funcionalidades: Funcionalidade[]
): Funcionalidade[] => {
  const defaultFuncionalidades: Funcionalidade[] = []
  const onlyAdded = tiposAcessoSelected.filter(tipo => {
    const currentIds = currentSelectedTiposAcessos.map(i => i.value)
    return !currentIds.includes(tipo.value)
  })

  const onlyAddedIds = onlyAdded.map(v => v.value)

  onlyAddedIds.forEach(tipoAcessoId => {
    const tipoAcesso = tiposAcesso.find(({ id }) => id === tipoAcessoId)

    if (tipoAcesso) {
      for (const padrao of tipoAcesso.funcionalidadesPadrao ?? []) {
        const funcionalidade = funcionalidades.find(
          ({ id }) => id === padrao?.id
        )

        if (funcionalidade) {
          defaultFuncionalidades.push(funcionalidade)
        }
      }
    }
  })

  return defaultFuncionalidades
}

export const handleRemovedTipoAcesso = (
  tiposAcesso: OptionSelect[],
  selectedFuncionalidades: Funcionalidade[]
) =>
  selectedFuncionalidades.filter(funcionalidade => {
    const tipoAcessosIds = tiposAcesso.map(tipo => tipo.value)
    return funcionalidadeHasAnyTipoAcessoId(funcionalidade, tipoAcessosIds)
  })

export const handleChangeTipoAcesso = (
  selectedTipos: OptionSelect[],
  selectedTiposAcessos: OptionSelect[],
  selectedFuncionalidades: Funcionalidade[],
  tiposAcesso: TipoAcesso[],
  funcionalidades: Funcionalidade[],
  updateSelectedFuncionalidades: (funcionalidades: Funcionalidade[]) => void,
  updateSelectedTiposAcesso: (tiposAcesso: OptionSelect[]) => void
) => {
  const updatedFuncionalidades = getFuncionalidadesPadraoByTipoAcesso(
    selectedTipos,
    selectedTiposAcessos,
    tiposAcesso,
    funcionalidades
  )

  handleRemovedTipoAcesso(selectedTipos, selectedFuncionalidades).forEach(
    toKeep => updatedFuncionalidades.push(toKeep)
  )

  const noDuplicates = updatedFuncionalidades.filter(
    (v, i, a) => a.findIndex(({ id }) => id === v.id) === i
  )

  updateSelectedFuncionalidades(noDuplicates)
  updateSelectedTiposAcesso(selectedTipos)
}

export function dealerIsGrupo(
  grupo: Grupo,
  idsGrupos: OptionSelect[]
): unknown {
  return idsGrupos.map(i => i.value).indexOf(grupo?.id ?? -1) > -1
}

export const syncCargosFromOptions = (
  options: OptionSelect[],
  cargos: CargoUsuarioForm[]
) =>
  options.reduce((acc: CargoUsuarioForm[], dealer) => {
    const foundCargo = cargos.find(
      (cd: CargoUsuarioForm) => cd.dealerId === dealer.value
    )
    if (foundCargo) {
      acc.push(foundCargo)
    } else {
      acc.push()
    }
    return acc
  }, [])
